import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouteContext } from "context/RouteProvider";
import { environment } from "environment/environment";
import {
  saveShopifyOnboardingSession,
  shopifyDashboardPath,
  shopifySignInPath,
} from "services/ShopifyOnboardingSession";
import logo from "../../assets/images/logo.png";

interface PublicIntegration {
  externalStoreId?: string;
  externalStoreDomain?: string | null;
  connectionStatus?: string;
}

interface RegistrationProfile {
  email?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  phone?: string;
}

const SHOP_DOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

const shopifyAdminUrl = (
  ...candidates: (string | null | undefined)[]
): string => {
  for (const candidate of candidates) {
    const domain = (candidate || "").trim().toLowerCase();
    if (SHOP_DOMAIN_PATTERN.test(domain)) {
      return `https://${domain}/admin/apps`;
    }
  }
  return "";
};

const ShopifyOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [, setIsProtected] = useContext(RouteContext);
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";
  const session = searchParams.get("session") || "";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [integration, setIntegration] = useState<PublicIntegration | null>(null);
  const [creatingAccount, setCreatingAccount] = useState(true);
  const [accountChoice, setAccountChoice] = useState<"new" | null>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const backToShopifyUrl = shopifyAdminUrl(
    integration?.externalStoreDomain,
    integration?.externalStoreId,
    shop
  );

  useEffect(() => {
    const load = async () => {
      if (!session) {
        setError("Missing onboarding session. Install the app from Shopify again.");
        setLoading(false);
        return;
      }
      saveShopifyOnboardingSession(session);
      let leaving = false;
      try {
        const response = await fetch(
          `${environment.api_url}/api/v1/integrations/shopify/session?session=${encodeURIComponent(
            session
          )}&shop=${encodeURIComponent(shop)}`
        );
        const body = await response.json();
        if (!response.ok || body.success === false) {
          setError(body.message || body.error || "This Shopify connection could not be opened.");
          setLoading(false);
          return;
        }
        const connected =
          body.data && body.data.integration ? body.data.integration : null;
        setIntegration(connected);
        const dashboardShop = shop || connected?.externalStoreId || "";
        if (body.data && body.data.verificationRequired) {
          const profileEmail =
            (body.data.profile && body.data.profile.email) || "";
          leaving = true;
          setIsProtected(true);
          navigate("/email-verification", {
            replace: true,
            state: { email: profileEmail },
          });
          return;
        }
        if (body.data && body.data.registrationRequired === false) {
          leaving = true;
          window.location.replace(shopifyDashboardPath(session, dashboardShop));
          return;
        }
        const profile: RegistrationProfile =
          (body.data && body.data.profile) || {};
        setCreatingAccount(body.data ? body.data.linkingRequired !== false : true);
        setEmail(profile.email || "");
        setFirstName(profile.firstName || "");
        setLastName(profile.lastName || "");
        setBusinessName(profile.businessName || "");
        setPhoneNumber(profile.phone || "");
      } catch {
        setError("Could not reach the Zulu API. Make sure the API is running on port 3015.");
      } finally {
        if (!leaving) {
          setLoading(false);
        }
      }
    };
    load();
  }, [navigate, session, shop]);

  const submitRegistration = async (event: React.FormEvent) => {
    event.preventDefault();
    if (creatingAccount && password !== confirmPassword) {
      setActionError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setActionError("");
    try {
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/shopify/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session,
            email,
            firstName,
            lastName,
            businessName,
            phoneNumber,
            password: creatingAccount ? password : undefined,
          }),
        }
      );
      const body = await response.json();
      if (!response.ok || body.success === false) {
        throw new Error(body.error || body.message || "Could not save this shop.");
      }
      const next = body.data && body.data.next;
      if (next === "verify") {
        setIsProtected(true);
        navigate("/email-verification", {
          replace: true,
          state: { email: (body.data && body.data.email) || email },
        });
        return;
      }
      if (next === "login") {
        window.location.assign(shopifySignInPath(session));
        return;
      }
      window.location.replace(
        shopifyDashboardPath(session, shop || integration?.externalStoreId || "")
      );
    } catch (requestError) {
      setActionError(
        requestError instanceof Error
          ? requestError.message
          : "Could not save this shop."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="text-center mb-4">
            <img src={logo} alt="Zulu Courier" style={{ maxHeight: 48 }} />
          </div>
          <div className="card shadow-sm">
            <div className="card-body p-4">
              {loading && <p className="text-muted mb-0">Checking the connection…</p>}
              {!loading && error && (
                <>
                  <div className="alert alert-danger mb-0">{error}</div>
                  {backToShopifyUrl && (
                    <div className="mt-4">
                      <a
                        className="btn btn-outline-secondary"
                        href={backToShopifyUrl}
                        target="_top"
                        rel="noopener noreferrer"
                      >
                        Back to Shopify
                      </a>
                    </div>
                  )}
                </>
              )}
              {!loading && !error && creatingAccount && accountChoice === null && (
                <div>
                  <h3 className="mb-2">Connect this shop to Zulu</h3>
                  <p className="text-muted">
                    Create a new Zulu business account, or sign in if this shop
                    owner already has one.
                  </p>
                  <p className="text-muted small mb-3">
                    Shop: {shop || integration?.externalStoreId || "—"}
                  </p>
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setAccountChoice("new")}
                    >
                      New Customer
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => navigate(shopifySignInPath(session))}
                    >
                      Existing Customer
                    </button>
                  </div>
                </div>
              )}
              {!loading && !error && (!creatingAccount || accountChoice === "new") && (
                <form className="mt-3" onSubmit={submitRegistration}>
                  <h3 className="mb-2">Create your Zulu business account</h3>
                  <p className="text-muted">
                    This registers the shop owner as a Zulu business customer. A
                    verification code is emailed before the dashboard opens.
                  </p>
                  {creatingAccount && (
                    <button
                      type="button"
                      className="btn btn-link px-0 mb-3"
                      onClick={() => setAccountChoice(null)}
                    >
                      Back
                    </button>
                  )}
                  <div className="mb-3">
                    <label className="form-label" htmlFor="owner-email">
                      Shop owner email
                    </label>
                    <input
                      id="owner-email"
                      className="form-control"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      required
                      maxLength={120}
                    />
                  </div>
                  <div className="row">
                    <div className="col-sm-6 mb-3">
                      <label className="form-label" htmlFor="owner-first">
                        First name
                      </label>
                      <input
                        id="owner-first"
                        className="form-control"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        autoComplete="given-name"
                        required
                        maxLength={60}
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <label className="form-label" htmlFor="owner-last">
                        Last name
                      </label>
                      <input
                        id="owner-last"
                        className="form-control"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        autoComplete="family-name"
                        required
                        maxLength={60}
                      />
                    </div>
                  </div>
                  {creatingAccount && (
                    <>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="business-name">
                          Business name
                        </label>
                        <input
                          id="business-name"
                          className="form-control"
                          value={businessName}
                          onChange={(event) => setBusinessName(event.target.value)}
                          autoComplete="organization"
                          required
                          maxLength={120}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="owner-phone">
                          Phone number
                        </label>
                        <input
                          id="owner-phone"
                          className="form-control"
                          value={phoneNumber}
                          onChange={(event) => setPhoneNumber(event.target.value)}
                          autoComplete="tel"
                          required
                        />
                      </div>
                    </>
                  )}
                  {creatingAccount && (
                    <div className="row">
                      <div className="col-sm-6 mb-3">
                        <label className="form-label" htmlFor="owner-password">
                          Password
                        </label>
                        <div className="password-input-container">
                          <input
                            id="owner-password"
                            className="form-control"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="new-password"
                            required
                            minLength={6}
                            maxLength={40}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((current) => !current)}
                          >
                            <PasswordEye open={showPassword} />
                          </button>
                        </div>
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label className="form-label" htmlFor="owner-confirm">
                          Confirm password
                        </label>
                        <div className="password-input-container">
                          <input
                            id="owner-confirm"
                            className="form-control"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            autoComplete="new-password"
                            required
                            minLength={6}
                            maxLength={40}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            aria-label={
                              showConfirmPassword
                                ? "Hide confirm password"
                                : "Show confirm password"
                            }
                            onClick={() =>
                              setShowConfirmPassword((current) => !current)
                            }
                          >
                            <PasswordEye open={showConfirmPassword} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-muted small mb-3">
                    Shop: {shop || integration?.externalStoreId || "—"}
                  </p>
                  {actionError && (
                    <div className="alert alert-danger">{actionError}</div>
                  )}
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={
                      submitting ||
                      !email.trim() ||
                      !firstName.trim() ||
                      !lastName.trim() ||
                      (creatingAccount &&
                        (!businessName.trim() ||
                          !phoneNumber.trim() ||
                          password.length < 6 ||
                          password !== confirmPassword))
                    }
                  >
                    {submitting
                      ? "Creating account…"
                      : creatingAccount
                      ? "Create account and open dashboard"
                      : "Save and open dashboard"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PasswordEye: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
    </svg>
  );

export default ShopifyOnboarding;
